import Modal from "@/components/atoms/Modal";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useSendCompanyCourseConventionForSignatureMutation } from "@/services/companies/courses/conventions";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Button as BsButton, Col, Form, Row } from "react-bootstrap";
import {
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface Programme {
  label: string;
  url: string;
}

interface SendCourseConventionForm {
  programmes: Programme[];
}

const SendCourseConventionModal: ForwardRefRenderFunction<
  IModalMethods,
  IModalChildrenProps<{ companyId: string; courseId: string }>
> = ({ companyId, courseId, onHide }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<SendCourseConventionForm>({
    defaultValues: { programmes: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "programmes",
  });

  const [sendForSignature] =
    useSendCompanyCourseConventionForSignatureMutation();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({ showOrHiddenModal }));

  const onSubmit: SubmitHandler<SendCourseConventionForm> = (data) => {
    const programmes = data.programmes?.length ? data.programmes : undefined;
    showOrHiddenModal();
    toast.promise(
      sendForSignature([companyId, courseId, { programmes }]).unwrap(),
      {
        pending: "Envoi de la convention pour signature en cours...",
        success: {
          render() {
            reset();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                La convention a été envoyée pour signature avec succès
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }
            const result = data?.data as { error?: string } | undefined;
            return (
              result?.error ||
              "Une erreur est survenue lors de l'envoi pour signature"
            );
          },
        },
      }
    );
  };

  const onError: SubmitErrorHandler<SendCourseConventionForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      onHide={onHide}
      title="Envoyer la convention pour signature"
      onSubmit={handleSubmit(onSubmit, onError)}
      beforeFooter={
        fields.length < 4 ? (
          <BsButton
            variant="primary"
            className="me-auto"
            onClick={() => append({ label: "", url: "" })}
          >
            + Ajouter un programme
          </BsButton>
        ) : undefined
      }
    >
      <Row className="gx-3">
        <Col lg={12}>
          {fields.length === 0 ? (
            <p className="text-700 fs--1 mb-0">
              Vous pouvez ajouter des programmes (optionnel), puis envoyer la
              convention pour signature.
            </p>
          ) : (
            <>
              <Form.Label>Programmes</Form.Label>
              {fields.map((field, index) => (
                <Row key={field.id} className="gx-2 mb-2 align-items-start">
                  <Col>
                    <Form.Control
                      type="text"
                      placeholder="Intitulé"
                      isInvalid={!!errors.programmes?.[index]?.label}
                      {...register(`programmes.${index}.label`, {
                        required: "L'intitulé est requis",
                      })}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.programmes?.[index]?.label?.message}
                    </Form.Control.Feedback>
                  </Col>
                  <Col>
                    <Form.Control
                      type="url"
                      placeholder="https://..."
                      isInvalid={!!errors.programmes?.[index]?.url}
                      {...register(`programmes.${index}.url`, {
                        required: "Le lien est requis",
                        pattern: {
                          value: /^https?:\/\/.+/,
                          message:
                            "L'URL doit commencer par http:// ou https://",
                        },
                      })}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.programmes?.[index]?.url?.message}
                    </Form.Control.Feedback>
                  </Col>
                  <Col xs="auto">
                    <BsButton
                      variant="link"
                      className="text-danger p-0 mt-1"
                      onClick={() => remove(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </BsButton>
                  </Col>
                </Row>
              ))}
            </>
          )}
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(SendCourseConventionModal);
